import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileSpreadsheet, RefreshCw, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, ChevronDown } from 'lucide-react'; 
import { GET } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import TenureSelect from '../components/TenureSelect';
import DateRangeFilter, { NO_CUSTOM_DATES } from '../components/DateRangeFilter';

let chapterReportMemory = null;

// --- Global Styles for Thin Scrollbar ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount || 0);
};

const DATE_RANGE_OPTIONS = [
    'This Week',
    'Last 1 Months',
    'Last 3 Months',
    'Last 6 Months',
    'Last 12 Months',
    'All Time'
];

// Admins below super-admin only get their assigned cities/chapters back from /report/*.
const isScopedAdmin = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role !== 'super-admin'; } catch { return false; }
};

// --- Helper to get Initials (e.g. Chetan Hadiya -> CH) ---
const getInitials = (fullName) => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ').filter(n => n); // Split by space and remove empty entries
    if (names.length === 0) return '';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

const ChapterReport = () => { 
    const navigate = useNavigate();
    const master = useMasterChapter();
    // A remembered report from under a different master chapter must not override it
    if (chapterReportMemory && chapterReportMemory.masterChapterId !== master.chapterId) chapterReportMemory = null;
    const [reportData, setReportData] = useState(chapterReportMemory?.reportData || []);
    const [loading, setLoading] = useState(false); 
    const [downloading, setDownloading] = useState(false);
      
    const [cities, setCities] = useState(chapterReportMemory?.cities || []);
    const [chapters, setChapters] = useState(chapterReportMemory?.chapters || []);
 
    const [dateRange, setDateRange] = useState(chapterReportMemory?.dateRange || 'Last 3 Months');
    const [tenureId, setTenureId] = useState(chapterReportMemory?.tenureId || '');
    const [customDates, setCustomDates] = useState(chapterReportMemory?.customDates || NO_CUSTOM_DATES);
    const [selectedCity, setSelectedCity] = useState(chapterReportMemory?.selectedCity || master.cityId);
    const [selectedChapter, setSelectedChapter] = useState(chapterReportMemory?.selectedChapter || master.chapterId);

    // Master chapter filter (super-admin header) locks the page's city/chapter filter.
    // Skips the first run so a restored chapterReportMemory isn't reset on mount.
    const appliedMasterRef = useRef(master.chapterId);
    useEffect(() => {
        if (appliedMasterRef.current === master.chapterId) return;
        appliedMasterRef.current = master.chapterId;
        setSelectedCity(master.cityId);
        setSelectedChapter(master.chapterId);
        setCurrentPage(1);
    }, [master.cityId, master.chapterId]);
      
    const [rowsPerPage, setRowsPerPage] = useState(chapterReportMemory?.rowsPerPage || 10);
    const [currentPage, setCurrentPage] = useState(chapterReportMemory?.currentPage || 1);
    const [totalPages, setTotalPages] = useState(chapterReportMemory?.totalPages || 1);
    const [totalRecords, setTotalRecords] = useState(chapterReportMemory?.totalRecords || 0);
    const [totals, setTotals] = useState(chapterReportMemory?.totals || null);

    // Sorting State
    const [sortConfig, setSortConfig] = useState(chapterReportMemory?.sortConfig || { key: 'business_name', direction: 'asc' });
    const [shouldFetch, setShouldFetch] = useState(!chapterReportMemory);
  
    useEffect(() => {
        const fetchCities = async () => {
            try {  
                const data = await GET('/report/cities');
                if (data) setCities(data);
            } catch (err) { 
                console.error("Error fetching cities:", err); 
            }
        };
        fetchCities();
    }, []); 
 
    useEffect(() => {
        const fetchChapters = async () => {
            if (!selectedCity) {
                setChapters([]);
                return;
            }
            try { 
                const data = await GET(`/report/chapters/city/${selectedCity}`);
                if (data) setChapters(data);
            } catch (err) { 
                console.error("Error fetching chapters:", err); 
            }
        };
        fetchChapters();
    }, [selectedCity]);

    // A sub-admin with access to just one city / chapter gets it pre-selected (also after Clear).
    useEffect(() => {
        if (!isScopedAdmin()) return;
        if (!selectedCity && cities.length === 1) setSelectedCity(cities[0]._id);
    }, [cities, selectedCity]);

    useEffect(() => {
        if (!isScopedAdmin()) return;
        if (selectedCity && !selectedChapter && chapters.length === 1
            && (chapters[0].city_id?._id || chapters[0].city_id) === selectedCity) setSelectedChapter(chapters[0]._id);
    }, [chapters, selectedCity, selectedChapter]);
  
    const fetchReportData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('dateRange', dateRange);
            if (tenureId) params.append('tenureId', tenureId);
            if (customDates.startDate) {
                params.append('startDate', customDates.startDate);
                params.append('endDate', customDates.endDate);
            }
            params.append('page', currentPage); 
            params.append('limit', rowsPerPage);
            
            params.append('sortBy', sortConfig.key);
            params.append('sortOrder', sortConfig.direction);
            
            if (selectedCity) params.append('city', selectedCity);
            if (selectedChapter) params.append('chapter', selectedChapter);

            const result = await GET(`/chapter-reports?${params.toString()}`);

            if (result && result.success) { 
                if (Array.isArray(result.data)) setReportData(result.data);
                if (result.pagination) {
                    setTotalPages(result.pagination.total_pages);
                    setTotalRecords(result.pagination.total_records);
                }
                if (result.totals) setTotals(result.totals);
            } else {
                setReportData([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };
 
    useEffect(() => {
        if (!shouldFetch) {
            setShouldFetch(true);
            return;
        }

        fetchReportData(); 
    }, [dateRange, tenureId, customDates, selectedCity, selectedChapter, currentPage, rowsPerPage, sortConfig, shouldFetch]);

    useEffect(() => {
        chapterReportMemory = {
            masterChapterId: master.chapterId,
            reportData,
            cities,
            chapters,
            dateRange,
            tenureId,
            customDates,
            selectedCity,
            selectedChapter,
            rowsPerPage,
            currentPage,
            totalPages,
            totalRecords,
            totals,
            sortConfig
        };
    }, [
        reportData,
        cities,
        chapters,
        dateRange,
        tenureId,
        customDates,
        selectedCity,
        selectedChapter,
        rowsPerPage,
        currentPage,
        totalPages,
        totalRecords,
        totals,
        sortConfig,
        master.chapterId
    ]);
 

    const handleClearFilters = () => {
        setDateRange('Last 3 Months');
        setTenureId('');
        setCustomDates(NO_CUSTOM_DATES);
        setSelectedCity(master.cityId);
        setSelectedChapter(master.chapterId);
        setCurrentPage(1);  
        setSortConfig({ key: 'business_name', direction: 'asc' });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setRowsPerPage(newLimit);
        setCurrentPage(1);  
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 ml-1" />;
        return sortConfig.direction === 'asc' 
            ? <ArrowUp className="w-3.5 h-3.5 text-blue-600 ml-1" />
            : <ArrowDown className="w-3.5 h-3.5 text-blue-600 ml-1" />;
    };
 
    const handleDownloadReport = async (type) => { 
        setDownloading(true);
        try {
            const params = new URLSearchParams(); 
            params.append('dateRange', dateRange);
            if (tenureId) params.append('tenureId', tenureId);
            if (customDates.startDate) {
                params.append('startDate', customDates.startDate);
                params.append('endDate', customDates.endDate);
            }
            params.append('sortBy', sortConfig.key);
            params.append('sortOrder', sortConfig.direction);
            
            if (selectedCity) params.append('city', selectedCity);
            if (selectedChapter) params.append('chapter', selectedChapter);

            const token = localStorage.getItem('authToken');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/export-chapter-reports?${params.toString()}`, {
                method: 'GET', 
                headers: { 'Authorization': token ? `Bearer ${token}` : '' }
            });

            if (!response.ok) throw new Error("Failed to download report");
 
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `chapter_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading CSV:", error);
            alert("Failed to download the report. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    // Table Header Definitions
    const tableHeaders = [
        { label: 'Member', key: 'full_name', align: 'left', className: 'border-r border-slate-100' },
        { label: 'Ref. Recv.', key: 'reference_received', align: 'center', className: 'bg-blue-50/50 text-blue-700' },
        { label: 'Ref. Given', key: 'reference_given', align: 'center', className: 'bg-blue-50/50 border-r border-blue-100 text-blue-700' },
        { label: 'Biz. Recv.', key: 'business_received', align: 'right', className: 'bg-emerald-50/50 text-emerald-700' },
        { label: 'Biz. Given', key: 'business_given', align: 'right', className: 'bg-emerald-50/50 border-r border-emerald-100 text-emerald-700' },
        { label: '1-to-1', key: 'one_to_one_count', align: 'center', className: 'bg-amber-50/50 text-amber-700' },
        { label: 'Joined', key: 'members_joined', align: 'center', className: 'bg-rose-50/50 text-rose-700' },
        { label: 'Absent', key: 'absent_count', align: 'center', className: 'bg-red-50/50 text-red-600 font-bold' },
    ];

    const openDetail = (member, detailType) => {
        if (!member?.user_id) return;
        const memberName = member.full_name || 'Member';
        const params = new URLSearchParams({
            memberName,
            dateRange
        });
        if (tenureId) params.set('tenureId', tenureId);
        if (customDates.startDate) {
            params.set('startDate', customDates.startDate);
            params.set('endDate', customDates.endDate);
        }
        if (selectedCity) params.set('city', selectedCity);
        if (selectedChapter) params.set('chapter', selectedChapter);

        navigate(`/admin/business-report/${member.user_id}/${detailType}?${params.toString()}`);
    };

    /**
     * Members joined is derived from each profile's `reference`, so there is no
     * editable record behind it — send the admin to that member's activity
     * record with the list already open, carrying the report's own filter.
     */
    const openMembersJoined = (member) => {
        if (!member?.user_id) return;
        const params = new URLSearchParams({ tab: 'activity', section: 'members_joined' });
        if (tenureId) params.set('tenureId', tenureId);
        else if (dateRange) params.set('dateRange', dateRange);
        if (!tenureId && customDates.startDate) {
            params.set('startDate', customDates.startDate);
            params.set('endDate', customDates.endDate);
        }
        navigate(`/admin/users/${member.user_id}?${params.toString()}`);
    };

    return (
        <div>
            <CustomStyles />
            
            <div className="space-y-5">
                
                {/* --- Filter & Action Section --- */}
                <div className="relative z-20 bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">
                    
                    {/* Header: Title Left, Download Buttons Right */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <Filter className="w-5 h-5" />
                            </div>
                            <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Report Filters</h2>
                        </div>

                        {/* Download Buttons Moved Here */}
                        <div className="flex gap-3">
                             <button 
                                onClick={() => handleDownloadReport('excel')} 
                                disabled={downloading}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-emerald-200 transition-colors disabled:opacity-50"
                            >
                                {downloading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <FileSpreadsheet className="w-4 h-4" />}
                                <span>{downloading ? 'Downloading...' : 'Excel'}</span>
                            </button>
                            <button 
                                onClick={() => handleDownloadReport('csv')} 
                                disabled={downloading}
                                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-colors disabled:opacity-50"
                            >
                                {downloading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4" />}
                                <span>{downloading ? 'Downloading...' : 'CSV'}</span>
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-5 items-end">
                        {/* Date Range (named or custom) — ignored by the API while a tenure is selected */}
                        <DateRangeFilter
                            ranges={DATE_RANGE_OPTIONS}
                            range={dateRange}
                            customDates={customDates}
                            disabled={Boolean(tenureId)}
                            note={tenureId ? 'Overridden by the selected tenure.' : ''}
                            onChange={(r, c) => { setDateRange(r); setCustomDates(c); setCurrentPage(1); }}
                        />

                        {/* Tenure — takes priority over Date Range */}
                        <TenureSelect
                            chapterId={selectedChapter}
                            value={tenureId}
                            onChange={(value) => { setTenureId(value); setCurrentPage(1); }}
                            className="relative group"
                        />

                        {/* City Filter */}
                        <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">City</label>
                            <div className="relative">
                                <select value={selectedCity} onChange={(e) => { setSelectedCity(e.target.value); setCurrentPage(1); }} disabled={!!master.chapterId} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <option value="">All Cities</option> 
                                    {cities.map((city) => <option key={city._id} value={city._id}>{city.name}</option>)}
                                </select>
                                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                            </div>
                        </div>

                        {/* Chapter Filter */}
                        <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Chapter</label>
                            <div className="relative">
                                <select value={selectedChapter} onChange={(e) => { setSelectedChapter(e.target.value); setCurrentPage(1); }} disabled={!!master.chapterId} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <option value="">All Chapters</option>
                                    {chapters.map((chap) => <option key={chap._id} value={chap._id}>{chap.name}</option>)}
                                </select>
                                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                            </div>
                        </div>

                        {/* Rows Per Page */}
                         <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Rows</label>
                            <div className="relative">
                                <select value={rowsPerPage} onChange={(e) => handleRowsPerPageChange(Number(e.target.value))} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700">
                                    <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                                </select>
                                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                            </div>
                        </div>

                        {/* Clear Filters */}
                        <div>
                            <button onClick={handleClearFilters} className="w-full flex items-center justify-center gap-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 py-2.5 px-4 rounded-xl transition-colors font-bold text-sm shadow-sm">
                                <X className="w-4 h-4" /> Clear
                            </button>
                        </div>
                    </div>
                    {master.chapterId && <div className="mt-3"><MasterChapterLockNote /></div>}
                </div>

                {/* --- Totals Summary Strip --- */}
                {totals && (
                    <div className="bg-white rounded-xl border border-slate-200 px-5 py-3">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                            <span className="text-xs font-semibold text-slate-500 pr-4 border-r border-slate-200">Summary</span>
                            {[
                                { label: 'Total Reference', value: totals.totalReferencePasses ?? 0 },
                                // { label: 'Ref. Given', value: totals.total_ref_given ?? 0 },
                                // { label: 'Biz. Recv', value: formatCurrency(totals.total_business_received ?? 0) },
                                { label: 'Biz. Given', value: formatCurrency(totals.total_business_given ?? 0) },
                                { label: '1-to-1', value: totals.totalHost ?? 0 },
                            ].map(({ label, value }, i, arr) => (
                                <div key={label} className="flex items-center gap-x-3">
                                    <span className="text-[15px] font-semibold text-slate-700">{label}</span>
                                    <span className="text-[15px] font-semibold text-slate-500">:</span>
                                    <span className="text-[15px] font-semibold text-slate-900">{value}</span>
                                    {i < arr.length - 1 && <span className="text-slate-300 pl-2">|</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- Main Table --- */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    {tableHeaders.map((header) => (
                                        <th 
                                            key={header.key} 
                                            onClick={() => handleSort(header.key)} 
                                            className={`px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors ${header.className}`}
                                        >
                                            <div className={`flex items-center ${header.align === 'center' ? 'justify-center' : header.align === 'right' ? 'justify-end' : 'justify-start'}`}>
                                                {header.label}
                                                {getSortIcon(header.key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                                {loading ? (
                                     <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400 font-medium">Loading report data...</td></tr>
                                ) : reportData.length > 0 ? (
                                    reportData.map((member) => (
                                        <tr key={member._id} className="hover:bg-slate-50/80 transition-colors group">
                                            <td className="px-5 py-3 border-r border-slate-100">
                                                <div className="flex items-center">
                                                    {/* Updated Image Logic: Use raw response or Initials Fallback */}
                                                    {member.profile_picture ? (
                                                        <img 
                                                            src={member.profile_picture} // Used raw based on "responce me aa raha hai wo hi show"
                                                            alt="" 
                                                            className="w-9 h-9 rounded-full mr-3 object-cover border border-slate-200 shadow-sm" 
                                                            onError={(e) => {
                                                                e.target.style.display='none';
                                                                e.target.nextSibling.style.display='flex'; // Show fallback if img fails
                                                            }} 
                                                        />
                                                    ) : null}
                                                    
                                                    {/* Fallback Initials (Shown if no pic OR if pic fails to load) */}
                                                    <div 
                                                        className="w-9 h-9 rounded-full mr-3 bg-blue-100 flex-shrink-0 items-center justify-center text-xs font-bold text-blue-600 shadow-sm"
                                                        style={{ display: member.profile_picture ? 'none' : 'flex' }}
                                                    >
                                                        {getInitials(member.full_name)}
                                                    </div>

                                                    <div className="min-w-0">
                                                        <div className="text-sm font-bold text-slate-800" title={member.business_name}>
                                                            {member.full_name}
                                                        </div>
                                                        <div className="tabular text-xs font-medium text-slate-500">
                                                            {member.personal_phone_number || '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            
                                            <td className="px-5 py-3 whitespace-nowrap text-center text-sm font-bold text-blue-700 bg-blue-50/30">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(member, 'reference-received')}
                                                    className="font-bold text-blue-700 hover:text-blue-800 hover:underline"
                                                >
                                                    {member.reference_received}
                                                </button>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-center text-sm font-medium text-slate-600 bg-blue-50/30 border-r border-blue-100">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(member, 'reference-given')}
                                                    className="font-bold text-blue-700 hover:text-blue-800 hover:underline"
                                                >
                                                    {member.reference_given}
                                                </button>
                                            </td>
                                            
                                            <td className="px-5 py-3 whitespace-nowrap text-right text-sm font-bold text-emerald-700 bg-emerald-50/30">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(member, 'business-received')}
                                                    className="font-bold text-emerald-700 hover:text-emerald-800 hover:underline"
                                                >
                                                    {formatCurrency(member.business_received)}
                                                </button>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-right text-sm font-medium text-slate-600 bg-emerald-50/30 border-r border-emerald-100">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(member, 'business-given')}
                                                    className="font-bold text-emerald-700 hover:text-emerald-800 hover:underline"
                                                >
                                                    {formatCurrency(member.business_given)}
                                                </button>
                                            </td>
                                            
                                            <td className="px-5 py-3 whitespace-nowrap text-center text-sm font-medium text-amber-700 bg-amber-50/30">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(member, 'one-to-one')}
                                                    className="font-bold text-amber-700 hover:text-amber-800 hover:underline"
                                                >
                                                    {member.one_to_one_count}
                                                </button>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-center text-sm font-medium text-rose-700 bg-rose-50/30">
                                                <button
                                                    type="button"
                                                    onClick={() => openMembersJoined(member)}
                                                    className="font-bold text-rose-700 hover:text-rose-800 hover:underline disabled:cursor-default disabled:text-slate-400 disabled:no-underline"
                                                    disabled={!member.members_joined}
                                                    title={member.members_joined ? 'See who joined through this member' : 'Nobody joined through this member in this period'}
                                                >
                                                    {member.members_joined ?? 0}
                                                </button>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-center text-sm font-extrabold text-red-600 bg-red-50/30">{member.absent_count}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400 font-medium">No report data found matching filters.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                         <div className="text-sm font-medium text-slate-500">
                             Showing <span className="font-bold text-slate-700">{reportData.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}</span> to <span className="font-bold text-slate-700">{Math.min(currentPage * rowsPerPage, totalRecords)}</span> of <span className="font-bold text-slate-700">{totalRecords}</span> results
                         </div>
                         <div className="flex items-center gap-2">
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)} 
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-sm font-bold text-slate-700 px-2">Page {currentPage} of {totalPages || 1}</span>
                            <button 
                                onClick={() => handlePageChange(currentPage + 1)} 
                                disabled={currentPage >= totalPages}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChapterReport;